/**
 * Сборка сервисов приложения: сессия, клиент API (типизированный из OpenAPI и запасной HTTP),
 * TanStack Query, SSE. Всё создаётся один раз при запуске (main.tsx) и в тестах — с подменами.
 */
import { createDodocClient, type DodocClient } from '@dodoc/api-client';
import type { QueryClient } from '@tanstack/react-query';
import type { i18n as I18nInstance } from 'i18next';
import { currentLocale } from '@/i18n';
import { createEmitter, type Emitter } from '@/shared/lib/emitter';
import { createAuthFetch } from './api/authFetch';
import { EventStreamClient, type EventSourceLike } from './api/eventStream';
import { createHttpRequest, type HttpFetch, type HttpRequest } from './api/http';
import { createNotificationsApi, type NotificationsApi } from './api/notificationsApi';
import { createQueryClient } from './api/queryClient';
import { createSupportApi, type SupportApi } from './api/supportApi';
import { appConfig } from './config';
import { HttpAuthApi, type AuthApi } from './session/authApi';
import { SessionStore } from './session/sessionStore';

export interface AppServices {
  i18n: I18nInstance;
  session: SessionStore;
  queryClient: QueryClient;
  /** Типизированный клиент (пути и типы из backend/openapi.json). */
  api: DodocClient;
  /** Запрос к эндпоинтам, которых ещё нет в OpenAPI. */
  request: HttpRequest;
  events: EventStreamClient;
  authApi: AuthApi;
  notifications: NotificationsApi;
  support: SupportApi;
  /** Ошибки мутаций без meta.silent — для общих уведомлений. */
  mutationErrors: Emitter<unknown>;
  dispose: () => void;
}

export interface CreateServicesOptions {
  i18n: I18nInstance;
  baseUrl?: string;
  authApi?: AuthApi;
  fetch?: HttpFetch;
  createEventSource?: (url: string) => EventSourceLike;
  crossTab?: boolean;
}

export function createAppServices(options: CreateServicesOptions): AppServices {
  const baseUrl = options.baseUrl ?? appConfig.apiBaseUrl;
  const getLocale = () => currentLocale(options.i18n);
  const authApi = options.authApi ?? new HttpAuthApi({ baseUrl, getLocale, fetch: options.fetch });
  const mutationErrors = createEmitter<unknown>();
  const queryClient = createQueryClient({ onMutationError: (error) => mutationErrors.emit(error) });

  let events: EventStreamClient | null = null;
  const session = new SessionStore({
    authApi,
    crossTab: options.crossTab ?? true,
    refreshLeewayMs: appConfig.refreshLeewayMs,
    onContextReset: () => {
      void queryClient.cancelQueries();
      queryClient.clear();
      events?.restart();
    },
  });

  const authFetch = createAuthFetch({
    getAccessToken: () => session.getState().accessToken,
    refresh: () => session.refreshAccessToken(),
    onMfaRequired: () => session.markMfaRequired(),
    onSessionRejected: (code) => session.endSession(code === 'SESSION_REVOKED' ? 'revoked' : 'expired'),
    baseFetch: options.fetch,
  });

  const api = createDodocClient({
    baseUrl,
    getAccessToken: () => session.getState().accessToken,
    getClinicId: () => session.getState().clinicId,
    getLocale,
    fetch: authFetch,
  });

  const request = createHttpRequest({
    baseUrl,
    fetch: authFetch,
    getHeaders: () => {
      const { accessToken, clinicId } = session.getState();
      const headers: Record<string, string> = { 'X-Locale': getLocale() };
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
      if (clinicId) headers['X-Clinic-Id'] = clinicId;
      return headers;
    },
  });

  events = new EventStreamClient({
    baseUrl,
    /* Билет выдаётся по заголовкам сессии и текущей клиники; на каждое подключение — новый (одноразовый, 60 с). */
    getTicket: async () => {
      if (!(await session.ensureFreshToken())) return null;
      const result = await api.POST('/api/v1/events/ticket', {});
      if (!result.response.ok || !result.data) throw new Error(`events ticket: HTTP ${result.response.status}`);
      return result.data.ticket;
    },
    getClinicId: () => session.getState().clinicId,
    createEventSource: options.createEventSource,
  });

  const streamClient = events;
  let streaming = false;
  let wasLocked = false;
  const unsubscribeSession = session.subscribe(() => {
    const { status, locked } = session.getState();
    const live = status === 'authenticated' && !locked;
    if (live && !streaming) {
      streaming = true;
      streamClient.start();
    } else if (!live && streaming) {
      streaming = false;
      streamClient.stop();
    }
    /* После разблокировки перечитать данные: пока экран был закрыт, запросы могли получить отказ. */
    if (wasLocked && !locked && status === 'authenticated') void queryClient.invalidateQueries();
    wasLocked = locked;
  });

  const onLanguageChanged = () => {
    void queryClient.invalidateQueries();
  };
  options.i18n.on('languageChanged', onLanguageChanged);

  return {
    i18n: options.i18n,
    session,
    queryClient,
    api,
    request,
    events: streamClient,
    authApi,
    notifications: createNotificationsApi(api),
    support: createSupportApi(api),
    mutationErrors,
    dispose: () => {
      unsubscribeSession();
      options.i18n.off('languageChanged', onLanguageChanged);
      streamClient.stop();
      session.dispose();
      queryClient.clear();
    },
  };
}
